import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useDeleteMutation, useToast } from "@/hooks";
import { DisplayError } from "@/utils";
import { PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

type AlertDialogDeleteProps = PropsWithChildren & {
  endpoint: string;
  queryKey?: string;
  callBack?: () => void;
};

export function AlertDialogDelete({
  endpoint,
  children,
  queryKey,
  callBack,
}: AlertDialogDeleteProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { mutateAsync, isLoading } = useDeleteMutation(endpoint, queryKey);

  async function handleDeleteData() {
    await mutateAsync(
      {
        endpoint,
      },
      {
        onSuccess: () => {
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.deleted successfully"),
          });
          if (typeof callBack === "function") callBack();
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error),
            variant: "destructive",
          });
        },
      }
    );
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("LABEL.are you sure?")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("MESSAGE.are you sure you want to delete?")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("LABEL.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteData}
            disabled={isLoading}
            isLoading={isLoading}
            className="bg-destructive hover:bg-destructive/80 text-white"
          >
            {t("LABEL.confirm")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
